import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { useActiveGroups, useConfig, useRetryGroupSync } from "../lib/api";
import { ChevronRight, Search, Loader2, X, RotateCcw } from "lucide-react";
import { STATUS_META, nb } from "../lib/journey";
import { DateRangePicker } from "../components/DateRangePicker";
import { GRANULARITIES, defaultRangeFor, parseCreatedAt, inRange, dailyActivityMap } from "../lib/dates";
import { useAuth } from "../lib/AuthContext";
import { normalizeGroupName } from "../lib/groupName";
import { useTenantTerms } from "../lib/TenantConfigContext";

export default function JourneyList() {
  const { me } = useAuth();
  const { unitLabel, unitLabelPlural } = useTenantTerms();
  const { data, isLoading, isError, error } = useActiveGroups();
  const { data: cfgData } = useConfig();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("All Locations");
  const [granularity, setGranularity] = useState("Day");
  const [range, setRange] = useState(() => defaultRangeFor("Day"));
  const [retryingRow, setRetryingRow] = useState(null);
  const retryGroupSync = useRetryGroupSync();
  const rows = useMemo(() => data?.rows || [], [data]);

  // Arriving from an Advisor Performance reason (e.g. "Estimate missing on 3 of 10 job
  // cards") — jump straight to exactly those job cards instead of the normal
  // location/date-scoped list, since the reason already picked out the ones that matter.
  const idsParam = searchParams.get("ids");
  const reasonParam = searchParams.get("reason");
  const actionRequiredIds = useMemo(
    () => (idsParam ? new Set(idsParam.split(",").map(Number).filter((n) => !Number.isNaN(n))) : null),
    [idsParam]
  );

  // Single-location roles: SA, CCM, WM, PSF, Receptionist
  const isSingleLocationRole = me?.role && ["sa", "ccm", "wm", "psf", "receptionist"].includes(me.role);
  const isGm = me?.role === "gm";
  const userWorkshopLocation = me?.workshop_location;
  const gmLocations = useMemo(
    () => (Array.isArray(me?.workshop_locations) ? me.workshop_locations.filter(Boolean) : []),
    [me]
  );

  // "Created At" only exists once the backend has been redeployed with the Supabase
  // created_at mapping — skip date filtering until then instead of showing an empty list.
  const hasDates = useMemo(() => rows.some((r) => parseCreatedAt(r)), [rows]);
  const activity = useMemo(() => dailyActivityMap(rows), [rows]);

  // Same as Overview: merge Stakeholders config locations with locations present on groups.
  // GM only sees workshops they are assigned to (from /auth/me).
  const configLocations = useMemo(
    () => (cfgData?.rows || []).map((r) => r["Workshop Location"]).filter(nb),
    [cfgData]
  );
  const locations = useMemo(() => {
    if (isGm) return gmLocations;
    return [...new Set([...configLocations, ...rows.map((r) => r["Workshop Location"]).filter(nb)])];
  }, [rows, configLocations, isGm, gmLocations]);

  const setGranularityAndRange = (g) => {
    setGranularity(g);
    setRange(defaultRangeFor(g));
  };

  const canRetrySync = (status) => status === "Pending Sync" || status === "Error";

  const handleRetrySync = async (e, rowId, groupName) => {
    e.preventDefault();
    e.stopPropagation();
    setRetryingRow(rowId);
    try {
      const data = await retryGroupSync.mutateAsync(rowId);
      const updated = data?.rows?.find((r) => r._row === rowId);
      const status = updated?.["Status"];
      if (status === "Created") {
        toast.success(`${groupName || "Group"} synced — WhatsApp group created.`);
      } else if (status === "Error") {
        toast.error(updated?.["Last Error"] || "Group creation failed. Try again or check Chat360.");
      } else {
        toast.message("Retry sent — still waiting for WhatsApp to confirm. You can retry again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Retry failed.");
    } finally {
      setRetryingRow(null);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();

    // Action-required view bypasses location/date scoping entirely — those job cards were
    // already picked out by the reason that linked here, and a default "Day" range would
    // otherwise hide ones created earlier. Search still narrows within that set.
    const normalizedS = normalizeGroupName(s);
    const matchesQuery = (r) =>
      (r["Group Name"] || "").toLowerCase().includes(s) ||
      (normalizedS && normalizeGroupName(r["Group Name"]).includes(normalizedS)) ||
      (r["Customer Name"] || "").toLowerCase().includes(s);

    if (actionRequiredIds) {
      let out = rows.filter((r) => actionRequiredIds.has(r._row));
      if (s) {
        out = out.filter(matchesQuery);
      }
      return out;
    }

    let out = rows;
    if (loc !== "All Locations") {
      out = out.filter((r) => r["Workshop Location"] === loc);
    }
    if (s) {
      out = out.filter(matchesQuery);
    }
    if (hasDates) {
      out = out.filter((r) => inRange(parseCreatedAt(r), range));
    }
    // Latest-created first: real Created At when available, falling back to row/append
    // order (higher _row = created later) so this still sorts sensibly pre-backend-deploy.
    return [...out].sort((a, b) => {
      const da = parseCreatedAt(a);
      const db = parseCreatedAt(b);
      if (da && db) return db - da;
      if (da) return -1;
      if (db) return 1;
      return b._row - a._row;
    });
  }, [rows, q, loc, range, hasDates, actionRequiredIds]);

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="journey-loading">
        <Loader2 className="animate-spin" size={18} /> Loading groups…
      </div>
    );
  if (isError)
    return (
      <div data-testid="journey-error" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 text-sm">
        Failed to load groups: {error?.response?.data?.detail || error.message}
      </div>
    );

  return (
    <div className="space-y-6" data-testid="journey-list-page">
      <div>
        <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Service Journey</h1>
      </div>

      {actionRequiredIds && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"
          data-testid="action-required-banner"
        >
          <span>
            Showing <span className="tabular-nums font-medium">{filtered.length}</span>{" "}
            {(filtered.length === 1 ? unitLabel : unitLabelPlural).toLowerCase()}
            {reasonParam ? <> where {reasonParam.toLowerCase()}</> : " that need action"}
          </span>
          <Link to="/journey" className="inline-flex items-center gap-1 font-medium text-amber-900 hover:underline" data-testid="clear-action-filter">
            <X size={14} /> Clear
          </Link>
        </div>
      )}

      {!actionRequiredIds && !isSingleLocationRole && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="location-filter">
          {["All Locations", ...locations].map((l) => (
            <button
              key={l}
              data-testid={`loc-pill-${l}`}
              onClick={() => setLoc(l)}
              className={`rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-colors border whitespace-nowrap shrink-0 ${
                loc === l
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
      {!actionRequiredIds && isSingleLocationRole && userWorkshopLocation && (
        <div className="flex gap-2" data-testid="location-filter">
          <button
            type="button"
            data-testid={`loc-pill-${userWorkshopLocation}`}
            className="rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium border whitespace-nowrap shrink-0 bg-blue-600 text-white border-blue-600 cursor-default"
          >
            Your Location: {userWorkshopLocation}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="journey-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by group or customer name…"
            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
          />
        </div>

        {!actionRequiredIds && (
          <div className="flex flex-wrap items-center gap-2" data-testid="date-controls">
            <div className="flex rounded-full border border-slate-200 bg-white p-0.5" data-testid="granularity-toggle">
              {GRANULARITIES.map((g) => (
                <button
                  key={g}
                  data-testid={`granularity-${g.toLowerCase()}`}
                  onClick={() => setGranularityAndRange(g)}
                  disabled={!hasDates}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    granularity === g ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            <DateRangePicker range={range} onChange={setRange} activity={activity} disabled={!hasDates} />
          </div>
        )}
      </div>
      {!actionRequiredIds && !hasDates && (
        <p className="text-xs text-slate-400" data-testid="dates-unavailable-note">
          Date filtering will switch on once groups carry a creation timestamp.
        </p>
      )}

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="journey-list">
        {filtered.map((r) => {
          const meta = STATUS_META[r["Status"]] || { label: r["Status"] || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
          const created = parseCreatedAt(r);
          const status = r["Status"];
          const showRetry = canRetrySync(status);
          const isRetrying = retryingRow === r._row;
          // Job card closed = feedback collected (§4.5's fuller "Delivered + Feedback"
          // definition isn't tracked as its own condition yet — see docs/backend-integration.md).
          const jobCardClosed = nb(r["Feedback Notified"]);
          return (
            <div
              key={r._row}
              data-testid={`journey-row-${r._row}`}
              className="flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
            >
              <Link to={`/journey/${r._row}`} className="flex flex-1 items-center justify-between min-w-0">
                <div className="min-w-0 pr-3">
                  <div className="flex items-center gap-1.5">
                    <div className="font-medium text-slate-800 truncate">{r["Group Name"] || "Untitled group"}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {r["Customer Name"] || "—"} · {r["Workshop Location"] || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {created && <span className="text-xs text-slate-400 tabular-nums hidden sm:inline">{format(created, "dd MMM, h:mm a")}</span>}
                  {jobCardClosed && (
                    <span
                      data-testid={`job-card-closed-${r._row}`}
                      className="hidden sm:inline-flex text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-violet-50 text-violet-700 border-violet-200"
                    >
                      {unitLabel} Closed
                    </span>
                  )}
                  <span className={`inline-flex text-[11px] font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`}>
                    {meta.label}
                  </span>
                </div>
              </Link>
              <div className="flex items-center gap-2 shrink-0 pl-2">
                {showRetry && (
                  <button
                    type="button"
                    onClick={(e) => handleRetrySync(e, r._row, r["Group Name"])}
                    disabled={isRetrying}
                    data-testid={`journey-retry-${r._row}`}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {isRetrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Retry
                  </button>
                )}
                <Link to={`/journey/${r._row}`} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Open journey">
                  <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          );
        })}
        {!filtered.length && <p className="text-sm text-slate-400 px-5 py-6">No groups match your search.</p>}
      </div>
    </div>
  );
}
